import { Component, ElementRef, QueryList, ViewChildren, AfterViewInit } from '@angular/core';
import { ShareddtaService } from '@app/services/shareddta.service';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DialogService } from '../../../services/dialog.service';
import { CountryPhoneCodes } from '@app/components/interface/country-phone-codes';
import { DriverService } from '@app/services/driver.service';
import { ToastService } from '@app/services/toast.service';
import { AuthServiceService } from '@app/services/auth-service.service';
import { BreadcrumbService } from '@app/services/breadcrumb.service';
export interface Driver {
  id?: string;
  driverType: 'associated' | 'head';
  isHeadDriver: boolean;
  firstName: string;
  lastName: string;
  countryCode: string;
  phone: string;
  confirmPhone: string;
  address?: string;
  email: string;
  password: string;
  profilePhoto?: File | null;
  documents?: { [key: string]: File | null };
}

export interface DocumentUpload {
  file: File | null;
  preview: string | null;
  type: 'image' | 'pdf' | null;
  filename: string | null;
}
interface DriverFormData {
  driverType: 'associated' | 'head';
  firstName: string;
  lastName: string;
  address: string;
  email: string;
  password: string;
  countryCode: string;
  phone: string;
  confirmPhone: string;
  profilePhoto?: string | File;
  documents: {
    license?: File;
    abstract?: File;
    certificate?: File;
    cvor?: File;
    safety?: File;
    insurance?: File;
    [key: string]: File | undefined;
  };
}

@Component({
  selector: 'app-add-driver-listing',
  templateUrl: './add-driver-listing.component.html',
  styleUrl: './add-driver-listing.component.scss'
})
export class AddDriverListingComponent implements AfterViewInit {
  @ViewChildren('documentInput') documentInputs!: QueryList<ElementRef>;
  isAddMode: boolean = true;
  id: Number;
  initialDriverForm: string = '';
  driverForm: DriverFormData = {
    driverType: 'associated',
    firstName: '',
    lastName: '',
    address: '',
    email: '',
    password: '',
    countryCode: '+1',
    phone: '',
    confirmPhone: '',
    documents: {}
  };

  profilePhotoPreview: string | null = null;
  documentPreviews: {
    [key: string]: {
      type: string;
      name: string;
      preview?: string;
      isFromApi?: boolean;
      apiId?: number;
    }
  } = {};


  countryCodes: any = CountryPhoneCodes;

  documentTypes = [
    { id: 'license', label: 'Licence' },
    { id: 'abstract', label: 'Abstract' },
    { id: 'certificate', label: 'Certificate' },
    { id: 'cvor', label: 'CVOR' },
    { id: 'safety', label: 'Safety' },
    { id: 'insurance', label: 'Insurance' }
  ];

  errors: { [key: string]: string } = {};
  loading = false; // Add this

  constructor(
    private router: Router,
    private location: Location,
    private dialogService: DialogService,
    public driverService: DriverService,
    private toast: ToastService,
    private authService: AuthServiceService,
    private route: ActivatedRoute,
    private shareddta: ShareddtaService,
    private breadcrumbService: BreadcrumbService
  ) { this.id = this.route.snapshot.params['id']; }

  ngAfterViewInit(): void {
    this.shareddta.signalPageReady();
  }

  ngOnInit(): void {
    this.breadcrumbService.clear();
    const savedCountryCode = localStorage.getItem('countryCode');
    if (savedCountryCode) {
      this.driverForm.countryCode = savedCountryCode;
    }
    if (this.id) {
      this.getDriverDetails(this.id);
      this.isAddMode = false;
    }
    this.initialDriverForm = JSON.stringify(this.driverForm);
  }
  extraDocCount = 0;

  addMoreDocument() {
    this.extraDocCount++;

    const newDoc = {
      id: 'Other' + this.extraDocCount,
      label: 'Other ' + this.extraDocCount
    };

    this.documentTypes.push(newDoc);
  }

  toggleDriverType(type: 'associated' | 'head'): void {
    this.driverForm.driverType = type;
  }

  onProfilePhotoChange(event: any): void {
    const file = event.target.files[0];
    this.driverForm.profilePhoto = file;
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.profilePhotoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  onDocumentChange(event: any, docType: string): void {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          this.documentPreviews[docType] = {
            type: 'image',
            name: file.name,
            preview: e.target.result
          };
        };
        reader.readAsDataURL(file);
      } else if (fileExtension === 'pdf') {
        this.documentPreviews[docType] = {
          type: 'pdf',
          name: file.name
        };
      } else {
        this.documentPreviews[docType] = {
          type: 'other',
          name: file.name
        };
      }

      this.driverForm.documents[docType as keyof typeof this.driverForm.documents] = file;
    }
  }

  removeDocument(docType: string): void {
    this.deleteDriverDocument(Number(this.documentPreviews[docType].apiId))
    delete this.documentPreviews[docType];
    delete this.driverForm.documents[docType as keyof typeof this.driverForm.documents];
  }
  deleteDriverDocument(id: number): void {
    if(id){
   this.driverService.deleteDocumentr(id).subscribe(
      (data: any) => {
        if (this.authService.resultCodeError(data)) {
          return;
        }

        this.toast.show('success', data.resultDescription);;
        // Optionally, refresh the driver list or navigate away
        // this.router.navigate(['/' + this.authService._isRoleName + '/driver']);
      },
      (error) => {
        this.authService.dataError(error);
      }
    );
    }
 
  }

  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  capitalizeFirstLetter(field: 'firstName' | 'lastName'): void {
    const value = this.driverForm[field];
    if (value && value.length > 0) {
      this.driverForm[field] = value.charAt(0).toUpperCase() + value.slice(1);
    }
  }

  validateForm(): boolean {
    this.errors = {};
    let isValid = true;

    // First Name validation
    if (!this.driverForm.firstName.trim()) {
      this.errors['firstName'] = 'First Name is required';
      isValid = false;
    } else if (this.driverForm.firstName[0] !== this.driverForm.firstName[0].toUpperCase()) {
      this.errors['firstName'] = 'First letter must be capitalized';
      isValid = false;
    }

    // Last Name validation
    if (!this.driverForm.lastName.trim()) {
      this.errors['lastName'] = 'Last Name is required';
      isValid = false;
    } else if (this.driverForm.lastName[0] !== this.driverForm.lastName[0].toUpperCase()) {
      this.errors['lastName'] = 'First letter must be capitalized';
      isValid = false;
    }

    if (!this.driverForm.address.trim()) {
      this.errors['address'] = 'Address is required';
      isValid = false;
    }

    if (!this.driverForm.email.trim()) {
      this.errors['email'] = 'Email is required';
      isValid = false;
    } else if (!this.validateEmail(this.driverForm.email)) {
      this.errors['email'] = 'Please enter a valid email';
      isValid = false;
    }

    // Password validation (min 6 chars)
    if (this.isAddMode && !this.driverForm.password.trim()) {
      this.errors['password'] = 'Password is required';
      isValid = false;
    } else if (this.isAddMode && this.driverForm.password.length < 6) {
      this.errors['password'] = 'Password must be at least 6 characters';
      isValid = false;
    }

    if (!this.driverForm.phone.trim()) {
      this.errors['phone'] = 'Phone Number is required';
      isValid = false;
    } else if (this.driverForm.phone.length !== 10) {
      this.errors['phone'] = 'Phone must be 10 digits';
      isValid = false;
    }

    if (!this.driverForm.confirmPhone.trim()) {
      this.errors['confirmPhone'] = 'Confirm Phone Number is required';
      isValid = false;
    } else if (this.driverForm.phone !== this.driverForm.confirmPhone) {
      this.errors['confirmPhone'] = 'Phone numbers must match';
      isValid = false;
    }

    return isValid;
  }

  onPhoneInput(event: any): void {
    event.target.value = event.target.value.replace(/[^0-9]/g, '');
  }

  submitDriver(): void {
    if (this.validateForm()) {
      this.loading = true; // Start loading
      let certIds: number[] = [];
      const formData = new FormData();
      formData.append('first_name', this.driverForm.firstName);
      formData.append('last_name', this.driverForm.lastName);
      formData.append('email', this.driverForm.email);
      formData.append('password', this.driverForm.password);
      formData.append('phone_number', this.driverForm.phone);
      formData.append('confirm_phone_number', this.driverForm.confirmPhone);
      formData.append('address', this.driverForm.address);
      formData.append('is_head_driver', this.driverForm.driverType === 'head' ? 'true' : 'false');

      formData.append('is_active', 'true');
      if (this.driverForm.profilePhoto) {
        formData.append('image', this.driverForm.profilePhoto);
      }
      if (this.driverForm.documents?.insurance) {
        formData.append('driver_insurance', this.driverForm.documents.insurance);
      }

      if (this.driverForm.documents?.license) {
        formData.append('driver_license', this.driverForm.documents.license);
      }

      if (this.driverForm.documents?.certificate) {
        formData.append('driver_certificate', this.driverForm.documents.certificate);
      }

      if (this.driverForm.documents?.abstract) {
        formData.append('driver_abstract', this.driverForm.documents.abstract);
      }

      if (this.driverForm.documents?.cvor) {
        formData.append('driver_cvor', this.driverForm.documents.cvor);
      }

      if (this.driverForm.documents?.safety) {
        formData.append('driver_safety', this.driverForm.documents.safety);
      }

      formData.append('country_code', this.driverForm.countryCode);
      const otherFiles = this.driverForm.documents
        ? Object.keys(this.driverForm.documents)
          .filter(
            (key) =>
              key.startsWith('Other') && this.driverForm.documents[key]
          )
          .map((key) => this.driverForm.documents[key])
        : [];

      otherFiles.forEach((file) => {
        if (file) {
          formData.append('other_certificates', file);
          certIds.push(Math.floor(Math.random() * 1000000000000000000));
        }
      });
      if (certIds.length > 0) {
        formData.append('other_cert_ids', JSON.stringify(certIds));
      }

      // Handle the add or edit mode
      if (this.isAddMode) {
        this.driverService.driverAdd(formData).subscribe(
          (data: any) => {
            this.loading = false; // Stop loading
            if (this.authService.resultCodeError(data)) {
              return;
            }
            this.toast.show('success', data.resultDescription);
            this.router.navigate(['/' + this.authService._isRoleName + '/driver']);
          },
          (error) => {
            this.loading = false; // Stop loading
            this.authService.dataError(error);
          }
        );
      } else {
        this.driverService.driverEdit(this.id, formData).subscribe(
          (data: any) => {
            this.loading = false; // Stop loading
            if (this.authService.resultCodeError(data)) {
              return;
            }
            this.driverService.forceRefreshListing = true;
            this.toast.show('success', data.resultDescription);
            this.router.navigate(['/' + this.authService._isRoleName + '/driver']);
          },
          (error) => {
            this.loading = false; // Stop loading
            this.authService.dataError(error);
          }
        );
      }
    } else {
      console.log('Form validation failed:', this.errors);
    }
  }
  getDriverDetails(driverId: any) {
    this.driverService.driverDetails(driverId).subscribe((res) => {
      if (res && res.result) {
        let driverDetails = res.result
        this.driverForm.firstName = driverDetails.first_name
        this.driverForm.lastName = driverDetails.last_name
        this.driverForm.email = driverDetails.email
        this.driverForm.phone = driverDetails.phone_number
        this.driverForm.confirmPhone = driverDetails.phone_number
        this.driverForm.address = driverDetails.address
        const groupId = driverDetails.groups?.[0]?.id;
        let driverTypeValue: "head" | "associated" = "associated";
        if (groupId === 3) {
          driverTypeValue = 'head';
        } else if (groupId === 4) {
          driverTypeValue = 'associated';
        }
        this.driverForm.driverType = driverTypeValue;
        this.profilePhotoPreview = driverDetails.profile_image;
        this.mapDocumentsForEdit(driverDetails)
        this.initialDriverForm = JSON.stringify(this.driverForm);
      }
    })
  }
  mapDocumentsForEdit(apiResponse: any) {

    this.documentPreviews = {};

    // Reset extra count every time edit open ho
    this.extraDocCount = 0;

    const certificates = apiResponse.certificate || [];

    certificates.forEach((doc: any) => {

      let docId = '';

      const name = doc.doc_name.toLowerCase();

      if (name === 'license') {
        docId = 'license';

      } else if (name === 'abstract') {
        docId = 'abstract';

      } else if (name === 'certificate') {
        docId = 'certificate';

      } else if (name === 'cvor') {
        docId = 'cvor';

      } else if (name === 'safety certificate' || name === 'safety') {
        docId = 'safety';

      } else if (name === 'insurance') {
        docId = 'insurance';

      } else {

        // 🔥 All Other documents (API me kuch bhi value ho)

        this.extraDocCount++;

        docId = 'other' + this.extraDocCount;

        this.documentTypes.push({
          id: docId,
          label: 'Other - ' + this.extraDocCount
        });
      }

      // File type detect
      let fileType = 'other';

      if (doc.image.endsWith('.pdf')) {
        fileType = 'pdf';
      } else if (
        doc.image.endsWith('.png') ||
        doc.image.endsWith('.jpg') ||
        doc.image.endsWith('.jpeg')
      ) {
        fileType = 'image';
      }

      this.documentPreviews[docId] = {
        preview: doc.image,
        type: fileType,
        name: doc.doc_name,
        isFromApi: true,
        apiId: doc.id
      };

    });
  }




  hasChanges(): boolean {
    const currentFormJson = JSON.stringify(this.driverForm);
    if (this.initialDriverForm !== currentFormJson) {
      return true;
    }

    if (this.driverForm.profilePhoto instanceof File) {
      return true;
    }

    const hasNewDocs = Object.values(this.driverForm.documents).some(doc => doc instanceof File);
    if (hasNewDocs) {
      return true;
    }

    return false;
  }

  async cancel(): Promise<void> {
    if (!this.hasChanges()) {
      this.navigateToDriverList();
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Discard Changes',
      'Are you sure you want to cancel? Any unsaved changes will be lost.'
    );

    if (confirmed.confirmed) {
      this.navigateToDriverList();
    }
  }

  private navigateToDriverList(): void {
    this.router.navigate(['/' + this.authService._isRoleName + '/driver']);
  }

  resetForm(): void {
    this.driverForm = {
      driverType: 'associated',
      firstName: '',
      lastName: '',
      address: '',
      email: '',
      password: '',
      countryCode: '+1',
      phone: '',
      confirmPhone: '',
      documents: {}
    };
    this.profilePhotoPreview = null;
    this.documentPreviews = {};
    this.errors = {};
  }

  async goBack(): Promise<void> {
    if (!this.hasChanges()) {
      this.navigateToDriverList();
      return;
    }

    const confirmed = await this.dialogService.confirm(
      'Discard Changes',
      'Are you sure you want to cancel? Any unsaved changes will be lost.'
    );

    if (confirmed.confirmed) {
      this.navigateToDriverList();
    }
  }
}